import { useParams, useNavigate } from "react-router-dom";
import { route } from "../../../../routes";
import { StepIndicator } from "./StepIndicator";
import { Step4Employment } from "./steps/Step4Employment/Step4Employment";
import { Step5Financial } from "./steps/Step5Financial/Step5Financial";
import { Step2Personal } from "./steps/Step2Personal/Step2Personal";
import { Step7Regulatory } from "./steps/Step7Regulatory/Step7Regulatory";
import { Step1SignUp } from "./steps/Step1SignUp/Step1SignUp";
import { Step3Personal } from "./steps/Step3Personal/Step3Personal";
import { Step6StockAndBondExperience } from "./steps/Step6StockAndBondExperience/Step6StockAndBondExperience";
import { Step8TradingPermissions } from "./steps/Step8TradingPermissions/Step8TradingPermissions";
import { userAuthState, UserAuthState } from "../../../../state";
import { DefaultValues, FormProvider, useForm } from "react-hook-form";
import { createCustomerApplicationFormStateSchema, CustomerApplicationFormState } from "../formState/formState";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRecoilState, useRecoilValue } from "recoil";
import { ValidStep1SignUpState, validStep1State } from "./steps/Step1SignUp/step1SignUpState";
import { StepSubmit } from "./steps/StepSubmit";
import {
  CustomerApplicationStepNumber,
  CUSTOMER_APPLICATION_STEPS,
  getCustomerApplicationNextStep,
  hasCustomerApplicationStepNext,
} from "./customerApplicationSteps";
import { useEffect } from "react";

interface GetElementProps {
  step: Exclude<CustomerApplicationStepNumber, 1>;
  userStateData: UserAuthState;
  areAllDisabled: boolean;
}

const StepElement: React.FC<GetElementProps> = ({ step, userStateData, areAllDisabled }) => {
  switch (step) {
    case 2:
      return <Step2Personal cognitoIdToken={userStateData.idToken} areAllDisabled={areAllDisabled} />;
    case 3:
      return <Step3Personal areAllDisabled={areAllDisabled} />;
    case 4:
      return <Step4Employment userStateData={userStateData} areAllDisabled={areAllDisabled} />;
    case 5:
      return <Step5Financial areAllDisabled={areAllDisabled} />;
    case 6:
      return <Step6StockAndBondExperience areAllDisabled={areAllDisabled} />;
    case 7:
      return <Step7Regulatory areAllDisabled={areAllDisabled} />;
    case 8:
      return <Step8TradingPermissions areAllDisabled={areAllDisabled} />;
  }
};

interface CustomerApplicationWizardProps {
  onStepSave: (step: CustomerApplicationStepNumber, data: CustomerApplicationFormState) => Promise<void>;
  onSubmit: (data: CustomerApplicationFormState) => Promise<void>;
  isLoading: boolean;
  defaultState: DefaultValues<CustomerApplicationFormState>;
  defaultStep1State: DefaultValues<ValidStep1SignUpState>;
}

export const CustomerApplicationWizard: React.FC<CustomerApplicationWizardProps> = ({
  onSubmit,
  onStepSave,
  isLoading,
  defaultState,
  defaultStep1State,
}) => {
  const { step: rawStep } = useParams();
  const navigate = useNavigate();

  const userAuthData = useRecoilValue<UserAuthState | null>(userAuthState);
  const [validStep1] = useRecoilState(validStep1State);

  const formMethods = useForm<CustomerApplicationFormState>({
    defaultValues: defaultState,
    mode: "onTouched",
    resolver: zodResolver(createCustomerApplicationFormStateSchema(validStep1 ?? undefined)),
  });

  useEffect(() => {
    formMethods.reset(defaultState);
  }, [defaultState, formMethods]);

  const step = Number(rawStep) as CustomerApplicationStepNumber;
  const hasNext = hasCustomerApplicationStepNext(step);

  const onStep1Submit = async () => {
    if (validStep1 !== null && userAuthData !== null) {
      await onStepSave(1, formMethods.watch());
      const nextStep = getCustomerApplicationNextStep(step);
      navigate(route.customerApplication(nextStep));
    }
  };

  const onStepSubmit = hasNext
    ? async () => {
        const isStepValid = await formMethods.trigger(`step${step}` as "step2", { shouldFocus: true });

        if (isStepValid) {
          await onStepSave(step, formMethods.watch());
          navigate(route.customerApplication(getCustomerApplicationNextStep(step)));
        }
      }
    : formMethods.handleSubmit(async (data) => {
        await onStepSave(step, data);
        onSubmit(data);
      });

  // Leave it here to debug the invalid state in the wizard
  // Once is this feature stable, we can remove it.

  if (userAuthData === null) {
    return null;
  }

  return (
    <div className="flex flex-col items-center py-6 md:py-12">
      <StepIndicator currentStep={step} steps={CUSTOMER_APPLICATION_STEPS} />

      {step === 1 ? (
        <Step1SignUp onSuccess={onStep1Submit} defaultStep1State={defaultStep1State} isLoading={isLoading} />
      ) : (
        <FormProvider {...formMethods}>
          <StepElement step={step} userStateData={userAuthData} areAllDisabled={isLoading} />
          <StepSubmit text={hasNext ? "Continue" : "Submit"} isLoading={isLoading} onClick={onStepSubmit} />
        </FormProvider>
      )}
    </div>
  );
};
